/**
 * -----------------------------------------------------------------------------
 * Roles Listing Page
 * -----------------------------------------------------------------------------
 * This page is used to list all the roles of the organization.
 */

import React, { useEffect } from "react";
import AdminTable from "../../../components/adminTable";
import Pagination, {
  TPaginationSelected,
} from "../../../components/pagination";
import { useTranslation } from "react-i18next";
import { APP_CONFIG } from "../../../constants/constants";
import HeaderView from "../../../components/admin/headerView";
import ListingPageSubHeader from "../../../components/listingPageSubHeader";
import ListingTableHeader from "../../../components/listingTableHeader";
import AdminsRolesCreateNew from "./createNewRole";
import { useDispatch, useSelector } from "react-redux";
import {
  TListingQueryParams,
  TModalsState,
  setListingQueryParams,
  setModalsData,
} from "../../../api/store/commonSlice";
import { useOrganizationRolesQuery } from "../../../api/network/adminApiServices";
import { TAdminTableRowData } from "../../../components/types";
import { OrganizationUser } from "../../../api/types/Admin";
import { useDebouncedCallback } from "use-debounce";
import { routeUrls } from "../../../navigation/routeUrls";
import { useLoggedInUserData } from "../../../utils/user";
import { TSelectboxOption } from "../../../components/admin/formFields";
import {
  EditListingColumnsButton,
  ModalEditListingColumns,
  TListingColumn,
} from "../../../components/modalEditListingColumns";
import {
  localStorageKeys,
  useLocalStorage,
} from "../../../utils/localStorageItems";
import { SortingStatusWithReset } from "../../../utils/adminListings";
import { TLegacyUserRole } from "../../../api/types/UserRole";
import { ACL_NAMES, useACL } from "../../../hooks/acl";
import { ROLE_ACCESS_LEVELS } from "../../admin-details/role/constants";

const all_columns = ["Role Title", "Description", "Total Users"];

const columnOrderingFields: Record<string, string> = {
  "Role Title": "roleid",
  Description: "description",
  // "Total Users": "" // intentionally excluded from sortability
};

const ScreenDashboardAdminRoles = () => {
  const { t } = useTranslation("translation", { keyPrefix: "admins.roles" });
  const modalsState = useSelector(
    (state: { commonReducer: { modals: TModalsState } }) =>
      state.commonReducer.modals
  );
  const listingQueryParams: TListingQueryParams = useSelector(
    (state: any) => state.commonReducer.listingQueryParams
  );
  const { roles: orgRolesQueryParams } = listingQueryParams;
  const dispatch = useDispatch();
  const listingWrapperRef = React.useRef<HTMLDivElement>(null);

  const { getUserACLAccessLevel } = useACL();

  // columns
  const { getLocalStorageItem } = useLocalStorage();
  const savedColumns = getLocalStorageItem(localStorageKeys.columns.Roles);
  const [columns, setColumns] = React.useState<TListingColumn[]>(
    all_columns.map((colItem, _) => {
      return {
        name: colItem,
        show: !!savedColumns ? savedColumns.includes(colItem) : true,
      };
    })
  );

  // preparing query params
  const thisUserOrganizationId = useLoggedInUserData("ownerOrganizationId");
  const debouncedSetSearchKeyword = useDebouncedCallback((value: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        roles: {
          ...orgRolesQueryParams,
          page: 1,
          search: value,
        },
      })
    );
  }, 500);

  const handleOrderingChange = (ordering?: string) => {
    dispatch(
      setListingQueryParams({
        ...listingQueryParams,
        roles: {
          ...orgRolesQueryParams,
          ordering,
          page: 1,
        },
      })
    );
  };

  const roleQueryPayload = { ...orgRolesQueryParams };
  // Explicity remove accountid from query params to get both system and user roles
  delete roleQueryPayload.accountid;

  // fetching roles data
  const { data: dataOrgRoles, isFetching: isFetchingOrgRoles } =
    useOrganizationRolesQuery(
      {
        ...roleQueryPayload,
        // 'search_fields' intentionally not added here as default search fields are defined in the API service
      },
      {
        skip: !thisUserOrganizationId,
        refetchOnMountOrArgChange: true,
      }
    );
  const { count, /*next, previous,*/ results } = dataOrgRoles || {};

  // smooth scroll listing to the top on page change
  useEffect(() => {
    listingWrapperRef?.current?.scrollTo({ behavior: "smooth", top: 0 });
  }, [dataOrgRoles]);

  // preparing table data
  const tableData: TAdminTableRowData[] = !!results
    ? (results || ([] as OrganizationUser[])).map(
        (item: TLegacyUserRole, index: number) => ({
          navLink: `${routeUrls.dashboardChildren.adminChildren.roles}/${item.roleid}`,
          cellData: [
            item?.roleid, // id:
            item?.description || "-", // description:
            item?.total_users || "-", // total users under role
          ].filter((_, index) => columns[index].show),
        })
      )
    : [];

  // rendering
  return (
    <>
      <HeaderView title={t("heading")} />
      <div className={`${APP_CONFIG.DES.DASH.P_HORIZ} pt-4`}>
        <ListingPageSubHeader
          heading={t("sub_heading")}
          {...(getUserACLAccessLevel(ACL_NAMES.ACL_ADMIN_ROLE) >=
            ROLE_ACCESS_LEVELS.ALL && {
            buttonText: t("add_new"),
            buttonCallback: () => {
              dispatch(setModalsData({ ...modalsState, showCreateRole: true }));
            },
          })}
        />
        <ListingTableHeader
          heading={
            <div className="flex items-center gap-4">
              {/* Heading */}
              <span>{t("listing_heading")}</span>

              {/* Sorted by label */}
              {!!orgRolesQueryParams?.ordering && (
                <SortingStatusWithReset
                  ordering={orgRolesQueryParams.ordering}
                  columnOrderingFields={columnOrderingFields}
                  onResetOrdering={() => handleOrderingChange(undefined)}
                />
              )}
            </div>
          }
          searchBoxPlaceholder={t("search_placeholder")}
          searchBoxValue={orgRolesQueryParams.search}
          searchBoxOnChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            debouncedSetSearchKeyword(e.target.value)
          }
          searchBoxOnClear={() => debouncedSetSearchKeyword("")}
        />
        <ModalEditListingColumns
          columns={columns}
          setColumns={setColumns}
          lsKey={localStorageKeys.columns.Users}
        />
        <div className="py-4 mt-4 relative">
          <EditListingColumnsButton disabled={isFetchingOrgRoles} />
          <AdminTable
            viewportHeightOffset={"400"}
            columns={columns
              .filter((item) => item.show)
              .map((item) => item.name)}
            data={tableData}
            isLoading={isFetchingOrgRoles}
            listingQueryParams={orgRolesQueryParams}
            listingWrapperRef={listingWrapperRef}
            onOrderingChange={handleOrderingChange}
            ordering={orgRolesQueryParams.ordering}
            sortableColumns={columnOrderingFields}
          />
          {!isFetchingOrgRoles && (
            <Pagination
              pageSize={orgRolesQueryParams.page_size}
              handlePageSizeChange={(e: TSelectboxOption | null) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    roles: {
                      ...orgRolesQueryParams,
                      page: 1,
                      page_size: parseInt(`${e?.value}`),
                    },
                  })
                );
              }}
              totalPages={
                count ? Math.ceil(count / orgRolesQueryParams.page_size) : 1
              }
              forcePage={orgRolesQueryParams.page - 1}
              handlePageClick={(data: TPaginationSelected) => {
                dispatch(
                  setListingQueryParams({
                    ...listingQueryParams,
                    roles: { ...orgRolesQueryParams, page: data?.selected + 1 },
                  })
                );
              }}
              paginCurrentPage={orgRolesQueryParams.page}
              paginItemsOnCurrentPage={results?.length || 0}
              paginTotalItemsCount={count}
            />
          )}
        </div>
      </div>
      <AdminsRolesCreateNew />
    </>
  );
};

export default ScreenDashboardAdminRoles;
